import {ReactElement, ReactNode, useMemo, useState} from 'react'
import {TablePagination, type TablePaginationProps} from '@components/ui/table-atoms'
import {Skeleton} from '@components/ui/skeleton'

export interface MobileTableProps<TData extends unknown[]> {
    data: TData
    rows: Array<{
        cell: (props: {
            dataItem: TData[number]
            index: number
            isRowExpanded: boolean
            toggleExpanded: () => void
        }) => ReactNode
        header: () => ReactNode
        id: string
    }>
    expandedRow?: (row: TData[number]) => ReactNode
    pagination?: TablePaginationProps['pagination']
    isLoading?: boolean
    emptyStateComponent?: ReactElement
    getRowId?: (dataItem: TData[number], index: number) => string | number
    className?: string
}

export const MobileTable = <TData extends unknown[]>({
    data,
    expandedRow,
    rows,
    pagination,
    isLoading,
    emptyStateComponent,
    getRowId,
    className
}: MobileTableProps<TData>) => {
    const tableData = useMemo(
        () => (isLoading ? Array.from({length: pagination?.limit ?? 50}) : data),
        [data, isLoading, pagination?.limit]
    )
    const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set())

    const toggleExpanded = (rowKey: string | number) =>
        setExpandedRows(prev => {
            const next = new Set(prev)
            if (next.has(rowKey)) {
                next.delete(rowKey)
            } else {
                next.add(rowKey)
            }
            return next
        })

    const isEmpty = !isLoading && tableData.length === 0

    return (
        <div className={className}>
            {isEmpty ? (
                emptyStateComponent
            ) : (
                <div className="flex flex-col gap-2">
                    {tableData.map((dataItem, rowIndex) => {
                        const rowKey = getRowId?.(dataItem, rowIndex) ?? rowIndex
                        const isRowExpanded = expandedRows.has(rowKey)

                        return (
                            <div
                                className="flex w-full flex-col gap-2 border-neutral-300 border-b px-0 py-4 first:border-t last:border-b-0"
                                key={rowKey}
                            >
                                {rows.map(row => (
                                    <div className="flex flex-col gap-1" key={row.id}>
                                        {row.header()}
                                        {isLoading ? (
                                            <Skeleton height={14} />
                                        ) : (
                                            row.cell({
                                                dataItem,
                                                index: rowIndex,
                                                isRowExpanded,
                                                toggleExpanded: () => toggleExpanded(rowKey)
                                            })
                                        )}
                                    </div>
                                ))}
                                {!isLoading && isRowExpanded && expandedRow?.(dataItem)}
                            </div>
                        )
                    })}
                </div>
            )}
            {!isEmpty && !!pagination && <TablePagination pagination={pagination} />}
        </div>
    )
}
